import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// A browser tab has a real download manager and Web Share API wired to the
// OS; a Capacitor WebView has neither by default — the classic <a download>
// blob-URL trick (and often navigator.share too) is silently inert there.
// The native path writes the file to app storage via Filesystem, then hands
// it to the OS share sheet via the Share plugin, which is how "download"
// actually has to work inside a native shell.
const isNative = Capacitor.isNativePlatform();

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function nativeShareBlob(filename: string, blob: Blob, title: string, text?: string) {
  const data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
  await Share.share({ title, text, url: uri });
}

export async function downloadBlob(filename: string, blob: Blob) {
  if (isNative) {
    try {
      await nativeShareBlob(filename, blob, filename);
      return;
    } catch {
      // fall through to the browser path below as a last resort
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Same native-share-first, download-fallback pattern as shareOrDownloadHtml,
// generalized to any blob/mime type (used for PDF receipts).
export async function shareOrDownloadBlob(filename: string, blob: Blob, mimeType: string, title: string, text?: string) {
  if (isNative) {
    try {
      await nativeShareBlob(filename, blob, title, text);
      return;
    } catch {
      // fall through
    }
  }
  const file = new File([blob], filename, { type: mimeType });
  const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
  if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
    }
  }
  await downloadBlob(filename, blob);
}

// Plain text+link share (no file involved) — used by Refer & Earn. Returns
// "shared" when a native share sheet actually opened, "copied" when it fell
// back to the clipboard (desktop browsers, or no Web Share support), so the
// caller can show the right toast either way.
export async function shareText(title: string, text: string, url: string): Promise<"shared" | "copied"> {
  if (isNative) {
    try {
      await Share.share({ title, text, url });
      return "shared";
    } catch {
      // fall through to clipboard
    }
  } else {
    const nav = navigator as Navigator & { share?: (data?: ShareData) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title, text, url });
        return "shared";
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return "shared";
      }
    }
  }
  await navigator.clipboard.writeText(`${text} ${url}`);
  return "copied";
}

export async function downloadHtml(filename: string, html: string) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  if (isNative) {
    try {
      await nativeShareBlob(filename, blob, filename);
      return;
    } catch {
      // fall through
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Tries the native share sheet (with the file attached) first — the
// natural way to hand a receipt to WhatsApp/email/etc on a phone. Falls
// back to a plain download wherever Web Share (or file-sharing) isn't
// supported, e.g. desktop browsers or this preview's sandboxed webview.
export async function shareOrDownloadHtml(filename: string, html: string, title: string, text?: string) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  if (isNative) {
    try {
      await nativeShareBlob(filename, blob, title, text);
      return;
    } catch {
      // fall through
    }
  }
  const file = new File([blob], filename, { type: "text/html" });
  const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
  if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
    }
  }
  await downloadHtml(filename, html);
}
